import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, Search, Star, Lock, FileText, MessageSquare, Calculator, ListChecks,
  Users as VendorIcon, Play, BookMarked, Loader2, Sparkles, Filter, X, Check, PartyPopper,
} from "lucide-react";

const CATEGORY_META = {
  contract:   { label: "Contracts",     singular: "Contract",    icon: FileText,      tone: "bg-amber-100 text-amber-800" },
  script:     { label: "Scripts",       singular: "Script",      icon: MessageSquare, tone: "bg-violet-100 text-violet-800" },
  calculator: { label: "Calculators",   singular: "Calculator",  icon: Calculator,    tone: "bg-emerald-100 text-emerald-800" },
  checklist:  { label: "Checklists",    singular: "Checklist",   icon: ListChecks,    tone: "bg-sky-100 text-sky-800" },
  vendor:     { label: "Vendors",       singular: "Vendor",      icon: VendorIcon,    tone: "bg-indigo-100 text-indigo-800" },
  video:      { label: "Videos",        singular: "Video",       icon: Play,          tone: "bg-rose-100 text-rose-800" },
  glossary:   { label: "Glossary",      singular: "Glossary",    icon: BookMarked,    tone: "bg-slate-200 text-slate-800" },
};

const TIER_GATE_LABEL = {
  free: { label: "Free", tone: "bg-slate-100 text-slate-700" },
  pro: { label: "Pro+", tone: "bg-emerald-100 text-emerald-800" },
  hustler: { label: "Hustler+", tone: "bg-sky-100 text-sky-800" },
  team: { label: "Team", tone: "bg-indigo-100 text-indigo-800" },
};

export default function Resources() {
  const [items, setItems] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [activeTrack, setActiveTrack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q) params.q = q;
      if (activeCategory) params.category = activeCategory;
      if (favoritesOnly) params.favorites_only = true;
      const [r1, r2, r3] = await Promise.all([
        api.get("/resources", { params }),
        api.get("/resources/categories"),
        api.get("/resources/tracks"),
      ]);
      setItems(r1.data || []);
      setCategoryCounts(r2.data?.categories || []);
      setTracks(r3.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load resources");
    } finally {
      setLoading(false);
    }
  }, [q, activeCategory, favoritesOnly]);

  useEffect(() => { refresh(); }, [refresh]);

  const featured = useMemo(
    () => items.filter((i) => i.featured).slice(0, 3),
    [items],
  );

  const activeTrackData = useMemo(
    () => tracks.find((t) => t.id === activeTrack) || null,
    [tracks, activeTrack],
  );

  // When a track is active, override the displayed items with the track's
  // ordered list (and hide search/category/featured noise to keep the focus).
  const displayedItems = activeTrackData ? activeTrackData.items : items;
  const showAuxFilters = !activeTrackData;

  return (
    <div className="flex flex-col gap-4" data-testid="resources-page">
      <div className="topbar">
        <div>
          <div className="crumb flex items-center gap-2">
            <BookOpen size={12} /> Learn · Resources
          </div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
            Wholesaler Resources
          </h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Templates, scripts, calculators, and vendor directories curated for you.
          </p>
        </div>
      </div>

      {/* Tracks banner — only when no track is active */}
      {!activeTrackData && tracks.length > 0 && (
        <div data-testid="resources-tracks-banner">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[var(--ink-2)] mb-2">
            <Sparkles size={12} /> Learning tracks
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {tracks.map((t) => (
              <TrackCard key={t.id} t={t} onClick={() => setActiveTrack(t.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Active track banner */}
      {activeTrackData && (
        <div
          className="card p-4 border-l-4 flex items-center gap-3 flex-wrap"
          style={{ borderLeftColor: trackTone(activeTrackData.color).border }}
          data-testid="resources-active-track"
        >
          <ProgressRing
            pct={activeTrackData.completion_pct || 0}
            color={trackTone(activeTrackData.color).border}
            emoji={activeTrackData.emoji}
          />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[var(--primary-dark)] font-bold mb-0.5">
              Active Track · {activeTrackData.viewed_count}/{activeTrackData.count} viewed · {activeTrackData.completion_pct}%
            </div>
            <div className="font-display font-extrabold text-xl leading-tight flex items-center gap-2 flex-wrap">
              {activeTrackData.label}
              {activeTrackData.is_complete && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-500 text-white" data-testid="resources-active-complete">
                  <PartyPopper size={10} /> Track complete
                </span>
              )}
            </div>
            <div className="text-sm text-[var(--ink-2)] line-clamp-2">{activeTrackData.description}</div>
          </div>
          <button
            type="button"
            className="btn btn-ghost shrink-0"
            onClick={() => setActiveTrack(null)}
            data-testid="resources-track-clear"
          >
            <X size={14} /> Exit track
          </button>
        </div>
      )}

      {/* Search + filter chips — hidden while a track is active */}
      {showAuxFilters && (
      <div className="card p-3 flex flex-col gap-3" data-testid="resources-toolbar">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, summary or tag…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]"
              data-testid="resources-search"
            />
          </div>
          <button
            type="button"
            className={"btn " + (favoritesOnly ? "btn-primary" : "btn-ghost")}
            onClick={() => setFavoritesOnly((v) => !v)}
            data-testid="resources-favorites-toggle"
          >
            <Star size={14} className={favoritesOnly ? "fill-current" : ""} />
            {favoritesOnly ? "My Library" : "Favorites"}
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5" data-testid="resources-category-chips">
          <CategoryChip active={activeCategory === ""} onClick={() => setActiveCategory("")} label="All" count={categoryCounts.reduce((a, c) => a + c.count, 0)} />
          {categoryCounts.map((c) => {
            const meta = CATEGORY_META[c.key];
            if (!meta) return null;
            return (
              <CategoryChip
                key={c.key}
                active={activeCategory === c.key}
                onClick={() => setActiveCategory(c.key === activeCategory ? "" : c.key)}
                label={meta.label}
                icon={meta.icon}
                count={c.count}
                tone={meta.tone}
              />
            );
          })}
        </div>
      </div>
      )}

      {/* Featured row — only when no filter/track is active */}
      {showAuxFilters && !q && !activeCategory && !favoritesOnly && featured.length > 0 && (
        <div data-testid="resources-featured">
          <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-[var(--ink-2)] mb-2">
            <Sparkles size={12} /> Featured
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {featured.map((r) => (
              <ResourceCard key={r.id} r={r} featured />
            ))}
          </div>
        </div>
      )}

      {/* Main grid */}
      {loading ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin inline" /> Loading resources…
        </div>
      ) : displayedItems.length === 0 ? (
        <div className="card p-12 text-center text-[var(--ink-2)]" data-testid="resources-empty">
          <Filter size={28} className="inline mb-2 opacity-60" />
          <div className="font-semibold">No resources match your filter</div>
          <div className="text-xs mt-1">Try clearing the search or category filter.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="resources-grid">
          {displayedItems.map((r, idx) => (
            <ResourceCard key={r.id} r={r} step={activeTrackData ? idx + 1 : null} />
          ))}
        </div>
      )}
    </div>
  );
}

function TrackCard({ t, onClick }) {
  const tone = trackTone(t.color);
  const pct = t.completion_pct || 0;
  const done = !!t.is_complete;
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left card p-3 hover:shadow-lg transition-all border-l-4 group relative overflow-hidden"
      style={{ borderLeftColor: tone.border }}
      data-testid={`resources-track-${t.id}`}
    >
      {done && (
        <div
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-500 text-white"
          data-testid={`resources-track-complete-${t.id}`}
        >
          <PartyPopper size={10} /> Complete
        </div>
      )}
      <div className="flex items-start gap-2 mb-1.5">
        <div className="shrink-0">
          <ProgressRing pct={pct} color={tone.border} emoji={t.emoji} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm leading-tight group-hover:text-[var(--primary)] transition-colors">
            {t.label}
          </div>
          <div className={"inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide " + tone.chip}>
            {t.viewed_count}/{t.count} · {pct}%
          </div>
        </div>
      </div>
      <div className="text-xs text-[var(--ink-2)] line-clamp-2 leading-snug">{t.description}</div>
    </button>
  );
}

function ProgressRing({ pct, color, emoji }) {
  // Conic-gradient ring — pure CSS, no SVG bloat.
  const safe = Math.max(0, Math.min(100, pct));
  const background = `conic-gradient(${color} ${safe}%, #E2E8F0 ${safe}% 100%)`;
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center"
      style={{ background }}
      data-testid="resources-track-ring"
      aria-label={`${safe}% complete`}
    >
      <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-base">
        {emoji}
      </div>
    </div>
  );
}

function trackTone(color) {
  return {
    sky:     { border: "#0EA5E9", chip: "bg-sky-50 text-sky-700" },
    emerald: { border: "#10B981", chip: "bg-emerald-50 text-emerald-700" },
    amber:   { border: "#F59E0B", chip: "bg-amber-50 text-amber-700" },
    violet:  { border: "#8B5CF6", chip: "bg-violet-50 text-violet-700" },
    rose:    { border: "#F43F5E", chip: "bg-rose-50 text-rose-700" },
  }[color] || { border: "#64748B", chip: "bg-slate-100 text-slate-700" };
}


function CategoryChip({ active, onClick, label, icon: Icon, count, tone }) {
  const base = active
    ? "bg-[var(--primary)] text-white border-[var(--primary)]"
    : "bg-white border-slate-200 text-[var(--ink-2)] hover:border-slate-400";
  return (
    <button
      type="button"
      onClick={onClick}
      className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + base}
      data-testid={`resources-chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {Icon && <Icon size={12} />}
      <span>{label}</span>
      {count != null && (
        <span className={"px-1.5 py-0.5 rounded-full text-[10px] font-bold " + (active ? "bg-white/20" : tone || "bg-slate-100")}>
          {count}
        </span>
      )}
    </button>
  );
}

function ResourceCard({ r, featured = false, step = null }) {
  const meta = CATEGORY_META[r.category] || { label: r.category, icon: BookOpen, tone: "bg-slate-100 text-slate-700" };
  const gate = TIER_GATE_LABEL[r.tier_gate] || TIER_GATE_LABEL.free;
  const Icon = meta.icon;
  return (
    <Link
      to={`/app/resources/${r.slug}`}
      className={"card p-4 hover:shadow-lg transition-shadow flex flex-col gap-2 group " + (featured ? "border-2 border-[var(--primary)]/30" : "")}
      data-testid="resource-card"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {step != null && (
            <div className="w-7 h-7 rounded-full bg-[var(--primary)] text-white font-display font-black text-xs flex items-center justify-center shrink-0" data-testid="resource-card-step">
              {step}
            </div>
          )}
          <div className={"w-9 h-9 rounded-lg flex items-center justify-center " + meta.tone}>
            <Icon size={16} />
          </div>
        </div>
        <div className="flex items-center gap-1">
          {r.is_viewed && (
            <div
              className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center"
              title="Viewed"
              data-testid="resource-card-viewed"
            >
              <Check size={12} strokeWidth={3} />
            </div>
          )}
          {r.is_favorited && <Star size={14} className="text-amber-500 fill-current" />}
          {r.is_locked && <Lock size={14} className="text-rose-500" />}
        </div>
      </div>
      <div className="font-display font-bold text-base leading-snug group-hover:text-[var(--primary)] transition-colors line-clamp-2">
        {r.title}
      </div>
      <div className="text-xs text-[var(--ink-2)] line-clamp-3 flex-1">
        {r.summary}
      </div>
      <div className="flex items-center justify-between mt-1 pt-2 border-t border-slate-100">
        <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold uppercase " + meta.tone}>
          {meta.singular || meta.label}
        </span>
        <span className={"px-2 py-0.5 rounded-full text-[10px] font-bold " + gate.tone}>
          {gate.label}
        </span>
      </div>
    </Link>
  );
}
